/**
 * CommunicationProvider Integration
 * Bridge between SDK and Zustand store; owns toast feedback for mutations.
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useCommunicationProviderStore } from '@/store/communication-provider.store';
import { useTenantStore } from '@/store/tenant.store';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class CommunicationProviderIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    _getTenantId() {
        return useTenantStore.getState().selectedTenantId;
    }

    async fetchProviders(page = 1, pageSize = 10, search = '', sortBy = 'createdAt', sortOrder = 'desc', filters = {}) {
        const store = useCommunicationProviderStore.getState();
        const tenantId = this._getTenantId();
        if (!tenantId) return { success: false };

        store.setLoading(true);
        store.clearError();
        try {
            const response = await this.service.getAll({ page, limit: pageSize, search, sortBy, sortOrder, ...filters });
            store.setCommunicationProviders(response.data || []);
            if (response.pagination) {
                const p = response.pagination;
                store.setPagination({
                    currentPage: p.page || page,
                    itemsPerPage: p.pageSize || pageSize,
                    totalItems: p.total || 0,
                    totalPages: p.totalPages || 0,
                    hasNextPage: (p.page < p.totalPages),
                    hasPreviousPage: (p.page > 1),
                });
            }
            if (response.uniqueValues) store.setUniqueValues(response.uniqueValues);
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async createProvider(data) {
        const store = useCommunicationProviderStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('communicationProvider:messages.createSuccess', { defaultValue: 'Provider created' }));
            store.closeForm();
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async updateProvider(id, data) {
        const store = useCommunicationProviderStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('communicationProvider:messages.updateSuccess', { defaultValue: 'Provider updated' }));
            store.closeForm();
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteProvider(id) {
        const store = useCommunicationProviderStore.getState();
        store.setLoading(true);
        try {
            await this.service.delete(id);
            toast.success(i18next.t('communicationProvider:messages.deleteSuccess', { defaultValue: 'Provider deleted' }));
            store.closeDeleteDialog();
            return { success: true };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async testProvider(id, payload) {
        const store = useCommunicationProviderStore.getState();
        store.setTestingId(id);
        try {
            await this.service.test(id, payload);
            toast.success(i18next.t('communicationProvider:messages.testSuccess', { defaultValue: 'Test message sent' }));
            return { success: true };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setTestingId(null);
        }
    }
}
